// stressTooltipContent.ts - Tooltip content generation for StressCountBadge
// Formats stress count data with per-word syllable breakdown and guidance

export interface WordBreakdown {
  /** Original word text */
  word: string
  /** Syllables with stress information */
  syllables: Array<{
    text: string
    stressed: boolean
  }>
}

export interface TooltipContent {
  /** Summary line (e.g., "3 stressed / 8 total syllables") */
  summary: string
  /** Per-word breakdown with formatted display (stressed syllables UPPERCASE) */
  words?: Array<{
    /** Original word */
    word: string
    /** Formatted display (e.g., "HEL-lo" with stressed uppercase) */
    display: string
  }>
  /** Optional guidance when stresses differ from expected */
  guidance?: string
  /** Match quality status for styling */
  status: 'good' | 'close' | 'needs-attention'
}

/**
 * Generate tooltip content for stress count badge.
 *
 * Creates structured tooltip data with summary, word-by-word breakdown,
 * and guidance when actual stresses differ from expected counts.
 *
 * @param stressedCount - Number of stressed syllables in line
 * @param totalCount - Total number of syllables in line
 * @param expectedCount - Expected stressed syllables (optional)
 * @param words - Per-word syllable breakdown (optional)
 * @returns Structured tooltip content with summary, breakdown, guidance, status
 */
export function generateTooltipContent(
  stressedCount: number,
  totalCount: number,
  expectedCount?: number,
  words?: WordBreakdown[]
): TooltipContent {
  // Build summary line
  const summary = `${stressedCount} stressed / ${totalCount} total syllables`

  // Determine match quality status
  const status = getMatchStatus(stressedCount, expectedCount)

  // Generate word breakdown if data available
  const wordDisplay = words?.map(word => ({
    word: word.word,
    display: formatWordWithStress(word.syllables),
  }))

  // Generate guidance when counts differ from expected
  const guidance = generateGuidance(stressedCount, expectedCount)

  return {
    summary,
    words: wordDisplay,
    guidance,
    status,
  }
}

/**
 * Determine match quality status based on difference from expected.
 *
 * Logic matches StressCountBadge.getMatchQuality():
 * - No expectations → good (neutral, show badge anyway)
 * - Within 1 syllable → close
 * - Difference > 1 → needs-attention
 */
function getMatchStatus(
  stressedCount: number,
  expectedCount?: number
): 'good' | 'close' | 'needs-attention' {
  if (expectedCount === undefined) {
    return 'good'
  }

  const difference = Math.abs(stressedCount - expectedCount)

  if (difference === 0) {
    return 'good'
  } else if (difference <= 1) {
    return 'close'
  } else {
    return 'needs-attention'
  }
}

/**
 * Format syllables with stressed syllables in UPPERCASE.
 *
 * Example: [{text: "hel", stressed: true}, {text: "lo", stressed: false}]
 * → "HEL-lo"
 */
function formatWordWithStress(syllables: Array<{ text: string; stressed: boolean }>): string {
  return syllables
    .map(syl => (syl.stressed ? syl.text.toUpperCase() : syl.text.toLowerCase()))
    .join('-')
}

/**
 * Generate guidance text when actual stresses differ from expected.
 *
 * Provides helpful context about stress count differences.
 */
function generateGuidance(stressedCount: number, expectedCount?: number): string | undefined {
  if (expectedCount === undefined) {
    return undefined
  }

  const difference = stressedCount - expectedCount

  if (difference === 0) {
    return undefined
  } else if (difference > 0) {
    const plural = difference === 1 ? 'syllable' : 'syllables'
    return `${difference} more stressed ${plural} than expected (${expectedCount})`
  } else {
    const plural = Math.abs(difference) === 1 ? 'syllable' : 'syllables'
    return `${Math.abs(difference)} fewer stressed ${plural} than expected (${expectedCount})`
  }
}
